import React, { useState, useEffect } from 'react';
import { X, Save, Plus, Trash2 } from 'lucide-react';
import type { ServiceProvider } from '../../services/graphql/userService';

interface ServiceProviderEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: ServiceProviderFormData) => Promise<void>;
  serviceProvider?: ServiceProvider | null;
  mode: 'create' | 'edit';
}

export interface ServiceProviderFormData {
  id?: string;
  serviceprovider_name: string;
  service_key: string;
  contact_email: string;
  contact_phone: string;
  primary_color: string;
  logo_url: string;
  is_active: boolean;
  max_distance: number;
  default_location: { lat: number; lng: number };
  default_zoom: number;
  timezone: string;
  attributes: {
    region: string;
    currencySymbol: string;
    vehicleTypes: string;
    paymentTypes: string;
    showContactInfo: boolean;
    showComfortLevels: boolean;
    enableGlobalSearch: boolean;
    defaultCommunicationMethod: string;
    communicationMethods: string[];
  };
}

const TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Australia/Sydney',
  'Pacific/Auckland',
  'Pacific/Fiji',
];

const COMMUNICATION_METHODS = ['voice', 'sms', 'email', 'whatsapp'];

const DEFAULT_FORM_DATA: ServiceProviderFormData = {
  serviceprovider_name: '',
  service_key: '',
  contact_email: '',
  contact_phone: '',
  primary_color: '#3b82f6',
  logo_url: '',
  is_active: true,
  max_distance: 100,
  default_location: { lat: 0, lng: 0 },
  default_zoom: 12,
  timezone: 'UTC',
  attributes: {
    region: '',
    currencySymbol: '$',
    vehicleTypes: '',
    paymentTypes: '',
    showContactInfo: true,
    showComfortLevels: true,
    enableGlobalSearch: false,
    defaultCommunicationMethod: 'email',
    communicationMethods: ['voice', 'email'],
  },
};

export const ServiceProviderEditor: React.FC<ServiceProviderEditorProps> = ({
  isOpen,
  onClose,
  onSave,
  serviceProvider,
  mode,
}) => {
  const [formData, setFormData] = useState<ServiceProviderFormData>(DEFAULT_FORM_DATA);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'basic' | 'location' | 'config' | 'features'>('basic');

  useEffect(() => {
    if (mode === 'edit' && serviceProvider) {
      const attrs = (serviceProvider.attributes || {}) as Record<string, unknown>;
      const location = typeof serviceProvider.default_location === 'object' && serviceProvider.default_location
        ? serviceProvider.default_location
        : { lat: 0, lng: 0 };

      setFormData({
        id: serviceProvider.id,
        serviceprovider_name: serviceProvider.serviceprovider_name || '',
        service_key: serviceProvider.service_key || '',
        contact_email: serviceProvider.contact_email || '',
        contact_phone: serviceProvider.contact_phone || '',
        primary_color: serviceProvider.primary_color || '#3b82f6',
        logo_url: serviceProvider.logo_url || '',
        is_active: serviceProvider.is_active ?? true,
        max_distance: serviceProvider.max_distance || 100,
        default_location: location,
        default_zoom: serviceProvider.default_zoom || 12,
        timezone: serviceProvider.timezone || 'UTC',
        attributes: {
          region: String(attrs.region || ''),
          currencySymbol: String(attrs.currencySymbol || '$'),
          vehicleTypes: String(attrs.vehicleTypes || ''),
          paymentTypes: String(attrs.paymentTypes || ''),
          showContactInfo: Boolean(attrs.showContactInfo ?? true),
          showComfortLevels: Boolean(attrs.showComfortLevels ?? true),
          enableGlobalSearch: Boolean(attrs.enableGlobalSearch ?? false),
          defaultCommunicationMethod: String(attrs.defaultCommunicationMethod || 'email'),
          communicationMethods: Array.isArray(attrs.communicationMethods)
            ? attrs.communicationMethods as string[]
            : ['voice', 'email'],
        },
      });
    } else {
      setFormData(DEFAULT_FORM_DATA);
    }
    setError(null);
  }, [mode, serviceProvider, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      // Validate required fields
      if (!formData.serviceprovider_name.trim()) {
        throw new Error('Service provider name is required');
      }
      if (!formData.service_key.trim()) {
        throw new Error('Service key is required');
      }
      if (!formData.contact_email.trim()) {
        throw new Error('Contact email is required');
      }

      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save service provider');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCommunicationMethodToggle = (method: string) => {
    const current = formData.attributes.communicationMethods;
    const updated = current.includes(method)
      ? current.filter(m => m !== method)
      : [...current, method];

    setFormData({
      ...formData,
      attributes: {
        ...formData.attributes,
        communicationMethods: updated,
      },
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-background border border-border rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {mode === 'create' ? 'Create Service Provider' : 'Edit Service Provider'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-6">
          {(['basic', 'location', 'config', 'features'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-3 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === tab
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="p-6 space-y-6">
            {error && (
              <div className="p-4 bg-destructive/10 text-destructive rounded-lg">
                {error}
              </div>
            )}

            {/* Basic Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Service Name *
                    </label>
                    <input
                      type="text"
                      value={formData.serviceprovider_name}
                      onChange={(e) => setFormData({ ...formData, serviceprovider_name: e.target.value })}
                      className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="ON Time Taxis & Tours"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Service Key *
                    </label>
                    <input
                      type="text"
                      value={formData.service_key}
                      onChange={(e) => setFormData({ ...formData, service_key: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      placeholder="fiji"
                      disabled={mode === 'edit'}
                    />
                    {mode === 'edit' && (
                      <p className="text-xs text-muted-foreground mt-1">Service key cannot be changed</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Contact Email *
                    </label>
                    <input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="contact@example.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Contact Phone
                    </label>
                    <input
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="+1-555-123-4567"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Primary Color
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="w-12 h-10 border border-input rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="flex-1 px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                        placeholder="#3b82f6"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Logo URL
                    </label>
                    <input
                      type="url"
                      value={formData.logo_url}
                      onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                      className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="https://example.com/logo.png"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-input"
                  />
                  <label htmlFor="is_active" className="text-sm font-medium text-foreground">
                    Active
                  </label>
                </div>
              </div>
            )}

            {/* Location Tab */}
            {activeTab === 'location' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Latitude
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={formData.default_location.lat}
                      onChange={(e) => setFormData({
                        ...formData,
                        default_location: { ...formData.default_location, lat: parseFloat(e.target.value) || 0 }
                      })}
                      className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="-17.7134"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Longitude
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={formData.default_location.lng}
                      onChange={(e) => setFormData({
                        ...formData,
                        default_location: { ...formData.default_location, lng: parseFloat(e.target.value) || 0 }
                      })}
                      className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="178.0650"
                    />
                  </div>
                </div>

                {formData.default_location.lat !== 0 && formData.default_location.lng !== 0 && (
                  <a
                    href={`https://www.google.com/maps?q=${formData.default_location.lat},${formData.default_location.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline text-sm"
                  >
                    View on Google Maps →
                  </a>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Default Zoom Level
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={formData.default_zoom}
                      onChange={(e) => setFormData({ ...formData, default_zoom: parseInt(e.target.value) || 12 })}
                      className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                    <p className="text-xs text-muted-foreground mt-1">1 = World, 20 = Building level</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Max Distance (km)
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.max_distance}
                      onChange={(e) => setFormData({ ...formData, max_distance: parseInt(e.target.value) || 100 })}
                      className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Timezone
                  </label>
                  <select
                    value={formData.timezone}
                    onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                    className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>{tz}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Config Tab */}
            {activeTab === 'config' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Region
                    </label>
                    <input
                      type="text"
                      value={formData.attributes.region}
                      onChange={(e) => setFormData({
                        ...formData,
                        attributes: { ...formData.attributes, region: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Fiji"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1">
                      Currency Symbol
                    </label>
                    <input
                      type="text"
                      value={formData.attributes.currencySymbol}
                      onChange={(e) => setFormData({
                        ...formData,
                        attributes: { ...formData.attributes, currencySymbol: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="$"
                      maxLength={3}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Vehicle Types
                  </label>
                  <input
                    type="text"
                    value={formData.attributes.vehicleTypes}
                    onChange={(e) => setFormData({
                      ...formData,
                      attributes: { ...formData.attributes, vehicleTypes: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="sedan,suv,luxury"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Comma-separated list</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Payment Types
                  </label>
                  <input
                    type="text"
                    value={formData.attributes.paymentTypes}
                    onChange={(e) => setFormData({
                      ...formData,
                      attributes: { ...formData.attributes, paymentTypes: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="cash,credit card,invoice"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Comma-separated list</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Communication Methods
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {COMMUNICATION_METHODS.map((method) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => handleCommunicationMethodToggle(method)}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                          formData.attributes.communicationMethods.includes(method)
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        {method}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Default Communication Method
                  </label>
                  <select
                    value={formData.attributes.defaultCommunicationMethod}
                    onChange={(e) => setFormData({
                      ...formData,
                      attributes: { ...formData.attributes, defaultCommunicationMethod: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-input rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    {COMMUNICATION_METHODS.map((method) => (
                      <option key={method} value={method}>{method}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Features Tab */}
            {activeTab === 'features' && (
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <div className="font-medium text-foreground">Show Contact Info</div>
                      <div className="text-sm text-muted-foreground">Display contact information on the booking page</div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.attributes.showContactInfo}
                        onChange={(e) => setFormData({
                          ...formData,
                          attributes: { ...formData.attributes, showContactInfo: e.target.checked }
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <div className="font-medium text-foreground">Show Comfort Levels</div>
                      <div className="text-sm text-muted-foreground">Allow users to select comfort level for rides</div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.attributes.showComfortLevels}
                        onChange={(e) => setFormData({
                          ...formData,
                          attributes: { ...formData.attributes, showComfortLevels: e.target.checked }
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <div className="font-medium text-foreground">Enable Global Search</div>
                      <div className="text-sm text-muted-foreground">Allow searching across all service areas</div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.attributes.enableGlobalSearch}
                        onChange={(e) => setFormData({
                          ...formData,
                          attributes: { ...formData.attributes, enableGlobalSearch: e.target.checked }
                        })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border bg-muted/30">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-input rounded-lg hover:bg-muted transition-colors"
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2 disabled:opacity-50"
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <span className="animate-spin">⏳</span>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {mode === 'create' ? 'Create' : 'Save Changes'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ServiceProviderEditor;
